import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import SvgGrid from '@material-ui/icons/GridOn';

import leaflet from 'leaflet';

const unitLength = 90;

const styles = (theme) => {
    return {
        root: {
            height: 32,
            width: 120,
            textAlign: 'center',
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.mainBorder,
            borderWidth: 1,
            borderStyle: 'solid',
        },
        scaleLabel: {
            position: 'absolute',
            top: 4,
            width: '100%',
            fontSize: 11,
            fontWeight: 400,
            color: theme.palette.primary.displayText,
        },
        bar: {
            position: 'absolute',
            width: unitLength + 2,
            height: 12,
            bottom: 4,
            left: 0,
            right: 0,
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        barSegment: {
            float: 'left',
            marginTop: 4,
            bottom: 0,
            height: 4,
            width: unitLength / 3,
            '&$barWhiteSegment': {
                backgroundColor: theme.palette.primary.displayText,
            },
            '&$barBlackSegment': {
                backgroundColor: theme.palette.primary.light,
            }
        },
        barEnd: {
            float: 'left',
            height: 8,
            width: 1,
            backgroundColor: theme.palette.primary.displayText,
        },
        barWhiteSegment: {},
        barBlackSegment: {},
        icon: {
            ...theme.iconStyle.size[18],
            ...theme.iconStyle.light,
            float: 'left',
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 12,
            paddingRight: 8,
        },
        gridLabel: {
            float: 'left',
            height: 32,
            lineHeight: '32px',
            fontSize: 12,
            fontWeight: 400,
            color: theme.palette.primary.displayText,
        },
    };
}

class ScaleControl extends Component {
    constructor(props) {
        super(props);

        this.state = {
            ratio: 0,
        };

        this.props.map.on({
            zoomend: this._handleZoomEnd,
        });
    }

    componentDidMount = () => {
        if (this.state.ratio === 0) {
            this._computeScale();
        }
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        return (this.state.ratio !== nextState.ratio ||
            this.props.gridMode !== nextProps.gridMode);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (this.state.ratio === 0) {
            this._computeScale();
        }
    }

    componentWillUnmount = () => {
        this.props.map.off({
            zoomend: this._handleZoomEnd,
        });
    }

    render = () => {
        if (this.state.ratio === 0) {
            return null;
        }

        const {classes} = this.props;

        return (
            <div className={this.props.className}>
                <Paper className={classes.root}>
                    {(this.props.gridMode) ? this._renderGridLayout() : this._renderScaleLayout()}
                </Paper>
            </div>
        );
    }

    _renderScaleLayout = () => {
        const {classes} = this.props;

        return (
            <div>
                <div className={classes.bar}>
                    <div className={classes.barEnd}/>
                    <div className={classNames(classes.barSegment, classes.barWhiteSegment)}/>
                    <div className={classNames(classes.barSegment, classes.barBlackSegment)}/>
                    <div className={classNames(classes.barSegment, classes.barWhiteSegment)}/>
                    <div className={classes.barEnd}/>
                </div>
                <Typography
                    className={classes.scaleLabel}
                    align='center'
                    variant='caption'
                >
                    {this.state.ratio}m
                </Typography>
            </div>
        );
    }

    _renderGridLayout = () => {
        const {classes} = this.props;

        return (
            <div>
                <SvgGrid className={classes.icon}/>
                <Typography
                    className={classes.gridLabel}
                    align='center'
                    variant='caption'
                >
                    Grid: 25m
                </Typography>
            </div>
        );
    }

    _handleZoomEnd = () => {
        this._computeScale();
    }

    _computeScale = async () => {
        const {map} = this.props;
        const {x, y} = map.getSize();
        const coord = {x: x/2 - unitLength/2, y: y/2};

        const latLng1 = map.containerPointToLatLng([coord.x, coord.y]);
        const latLng2 = map.containerPointToLatLng([coord.x + unitLength, coord.y]);
        const rawDistance = map.distance(latLng1, latLng2);
        const prettyDistance = (rawDistance <= 1000) ?
            Number(rawDistance).toFixed(0) :
            Number(rawDistance / 1000).toFixed(0) * 1000;

        this.setState({
            ratio: prettyDistance,
        });
    }
}

ScaleControl.propTypes = {
    map: PropTypes.instanceOf(leaflet.Map).isRequired,
    gridMode: PropTypes.bool.isRequired,
}

export default withStyles(styles)(ScaleControl);
